import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import { APP_ID, ApplicationConfig, ErrorHandler, importProvidersFrom, Injectable, InjectionToken, Injector, LOCALE_ID, PLATFORM_ID, provideZoneChangeDetection } from "@angular/core";
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, NativeDateAdapter } from "@angular/material/core";
import { provideShareButtonsOptions, SharerMethods, withConfig } from "ngx-sharebuttons";
import { IMAGE_LOADER, ImageLoaderConfig, isPlatformBrowser } from "@angular/common";
import { PreloadAllModules, provideRouter, RouteReuseStrategy, Routes, withInMemoryScrolling, withPreloading } from "@angular/router";
import { CustomReuseStrategy } from "./app/route-reuse-strategy";
import { environment } from "./environments/environment";
import { provideClientHydration, withEventReplay, withI18nSupport, withIncrementalHydration } from "@angular/platform-browser";
import { RUMService } from "./app/_services/rum.service";
import { enGB, et } from "date-fns/locale";
import { DefaultAPIErrorHandler, JwtInterceptor, LanguageInterceptor } from "./app/_interceptors";
import { OAuthModule } from "angular-oauth2-oidc";
import { Angulartics2Module } from "angulartics2";
import { provideAnimations } from "@angular/platform-browser/animations";
import { AuthGuard } from "./app/_guards";
import { shareIcons } from 'ngx-sharebuttons/icons';
import { snsLandingVariantResolver } from "./app/modules/snap-and-sell-landing/experiment.resolver";

const routes: Routes = [
  {path: 'marketplace', loadComponent: () => import('./app/modules/search/components/page/search-page.component').then(m => m.SearchPageComponent) },
  {path: 'privacy', loadComponent: () => import('./app/modules/privacy/components/page/privacy-page.component').then(m => m.PrivacyPageComponent)},
  {path: 'tac', loadComponent: () => import('./app/modules/tac/components/page/tac-page.component').then(m => m.TACPageComponent)},
  {path: 'items-to-sell', loadComponent: () => import('./app/modules/items-to-sell/components/page/items-to-sell-page.component').then(m => m.ItemsToSellPageComponent), canActivate: [AuthGuard]},
  {path: 'edit-listing/:id', loadComponent: () => import('./app/modules/edit-listing/components/page/edit-listing-page.component').then(m => m.EditListingPageComponent)},
  {path: 'create-listing/:id', loadComponent: () => import('./app/modules/create-listing/components/page/create-listing-page.component').then(m => m.CreateListingPageComponent), canActivate: [AuthGuard]},
  {path: 'view-listing/:itemId', loadComponent: () => import('./app/modules/view-listing/components/page/view-listing-page.component').then(m => m.ViewListingPageComponent)},
  {path: 'login', loadComponent: () => import('./app/modules/login/components/login.component').then(m => m.LoginComponent)},
  {path: 'login/callback', loadComponent: () => import('./app/modules/login/components/callback/callback.component').then(m => m.CallbackComponent)},
  {path: 'admin', loadComponent: () => import('./app/modules/admin/components/page/admin-page.component').then(m => m.AdminPageComponent), canActivate: [AuthGuard]},
  {path: 'account', loadComponent: () => import('./app/modules/account/components/page/account-page.component').then(m => m.AccountPageComponent), canActivate: [AuthGuard]},
  {path: 'about', loadComponent: () => import('./app/modules/global-warming/components/page/global-warming-page.component').then(m => m.GlobalWarmingPageComponent)},
  {path: 'contact', loadComponent: () => import('./app/modules/contact/components/page/contact-page.component').then(m => m.ContactPageComponent)},
  {path: 'chats', loadComponent: () => import('./app/modules/listing-chats/components/all-chats-page/all-chats-page.component').then(m => m.AllChatsPageComponent), canActivate: [AuthGuard]},
  {path: 'chats/listings/:itemId', loadComponent: () => import('./app/modules/listing-chats/components/listing-chats/listing-chats-page.component').then(m => m.ListingChatsPageComponent), canActivate: [AuthGuard]},
  {path: 'chats/listings/:itemId', loadComponent: () => import('./app/modules/listing-chats/components/listing-chats/listing-chats-page.component').then(m => m.ListingChatsPageComponent), canActivate: [AuthGuard]},
  {path: 'chats/listings/:itemId/chats/:chatId', loadComponent: () => import('./app/modules/listing-chats/components/seller-listing-chat-page/seller-listing-chat-page.component').then(m => m.SellerListingChatPageComponent), canActivate: [AuthGuard]},
  {path: 'why-trust', loadComponent: () => import('./app/modules/why-trust/components/page/why-trust-page.component').then(m => m.WhyTrustPageComponent)},
  {path: 'snap-and-sell', loadComponent: () => import('./app/modules/snap-and-sell/components/page/snap-and-sell-page.component').then(m => m.SnapAndSellPageComponent)},
  {path: 'snap-and-sell-v2', loadComponent: () => import('./app/modules/snap-and-sell-v2/components/page/snap-and-sell-page-v2.component').then(m => m.SnapAndSellPageV2Component)},
  {path: 'snap-and-sell-v2/success', loadComponent: () => import('./app/modules/snap-and-sell-v2/components/snap-and-sell-success-page/snap-and-sell-success-page.component').then(m => m.SnapAndSellSuccessPageComponent), canActivate: [AuthGuard]},
  {path: '', loadComponent: () => import('./app/modules/snap-and-sell-landing/components/page/snap-and-sell-landing.component').then(m => m.SnapAndSellLangingPageComponent), resolve: { variant: snsLandingVariantResolver }},
  {path: 'listing-created', loadComponent: () => import('./app/modules/listing-created/components/page/listing-created-page.component').then(m => m.ListingCreatedPageComponent), canActivate: [AuthGuard]},
  {path: 'cookie-policy', loadComponent: () => import('./app/modules/cookie-policy/components/page/cookie-policy-page.component').then(m => m.CookiePolicyPageComponent)},
  {path: 'snap-and-sell-ad', loadComponent: () => import('./app/modules/snap-and-sell-ad/components/page/snap-and-sell-ad-page.component').then(m => m.SnapAndSellAdPageComponent)},
  {path: 'green-box', loadComponent: () => import('./app/modules/green-box/components/page/green-box-page.component').then(m => m.GreenBoxPageComponent)},
  {path: 'green-box/tac', loadComponent: () => import('./app/modules/green-box/components/tac/tac-page.component').then(m => m.TacPageComponent)},
  //{path: 'resellers', loadChildren: () => import('./app/modules/resellers/resellers.module').then(m => m.ResellersModule)},
  {path: 'sales-partners', loadComponent: () => import('./app/modules/sales-partners/components/page/sales-partners-page.component').then(m => m.SalesPartnersPageComponent)},
  {path: '**', redirectTo: '/'}
];

@Injectable({providedIn: 'root'})
export class GlobalErrorHandler implements ErrorHandler {
  
  constructor(private injector: Injector) { } 
  
  handleError(error: any) {
    
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    if (chunkFailedMessage.test(error.message)) {
      if(confirm("New version available. Load New Version?")) {
        window.location.reload();
      }
    }
    
    this.injector.get(RUMService).handleError(error);
    console.error(error);
  }
}

export const DATE_FNS_LOCALE = new InjectionToken<Locale>('DATE_FNS_LOCALE');

export function mapLocaleIdToDateFns(localeId: string): Locale {
  const localeMap: Record<string, Locale> = {
    'en-US': enGB,
    'et': et
  };

  return localeMap[localeId];
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      OAuthModule.forRoot({
        // keep your existing configuration object here
        resourceServer: {
          allowedUrls: ['http://www.angular.at/api'],
          sendAccessToken: true
        }
      })
    ),
    importProvidersFrom(
      Angulartics2Module.forRoot({
        gst: {
          trackingIds: environment.gtag.trackingIds,
          anonymizeIp: true
        }
    }),),
    provideAnimations(),
    { provide: APP_ID, useValue: 'serverApp' },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DefaultAPIErrorHandler, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    provideShareButtonsOptions(
      shareIcons(),
      withConfig({
        sharerMethod: SharerMethods.Window,
        sharerTarget: 'newwindow'
      })
    ),
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: DATE_FNS_LOCALE, useFactory: (localeId: string) => mapLocaleIdToDateFns(localeId), deps: [LOCALE_ID] },
    {
        provide: IMAGE_LOADER,
        useValue: (config: ImageLoaderConfig) => {
            const fileName = config.width ? config.src.replace('.webp', '_' + config.width + ".webp") : config.src;
            return `${environment.assetsBaseURL}${fileName}`;
        },
    },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: 'API_URL', useFactory: (platformId: any) => { return isPlatformBrowser(platformId) ? environment.apiContext : 'http://backend:8081/api'; }, deps: [PLATFORM_ID] },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(withI18nSupport(), withIncrementalHydration(), withEventReplay()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({ 
        scrollPositionRestoration: 'enabled', 
        anchorScrolling: 'enabled' 
      }),
      withPreloading(PreloadAllModules)
    ),
  ]
};